import React, { Fragment, FunctionComponent } from 'react';
import { Typography, Grid, Accordion, AccordionSummary, AccordionDetails } from '@material-ui/core';
import ExpandMoreIcon from '@material-ui/icons/ExpandMore';
import TcmCoverLetterEntity from '../../../../../entities/tcm/TcmCoverLetter.Entity';
import TcmMedicalInfoEntity from '../../../../../entities/tcm/TcmMedicalInfo.Entity';
import TcmTreatingProviderEntity from '../../../../../entities/tcm/TcmTreatingProvider.Entity';
import TcmOfficeVisitEntity from '../../../../../entities/tcm/TcmOfficeVisit.Entity';
import TcmWorkStatusEntity from '../../../../../entities/tcm/TcmWorkStatus.Entity';
import TcmTelephonicCaseManagementEntity from '../../../../../entities/tcm/TcmTelephonicCaseManagement.Entity';
import { TcmCoverLetter } from './tcm-cover-letter/TcmCoverLetter.Component';
import { TcmMedicalInfo } from './tcm-medical-info/TcmMedicalInfo.Component';
import { TcmTreatingProvider } from './tcm-treating-provider/TcmTreatingProvider.Component';
import { TcmOfficeVisit } from './tcm-office-visit/TcmOfficeVisit.Component';
import { TcmWorkStatus } from './tcm-work-status/TcmWorkStatus.Component';
import { TcmTelephonicCaseManagement } from './tcm-telephonic-case-management/TcmTelephonicCaseManagement.Component';
import styles from './TcmReferralForm.Styles';
import StateEntity from '../../../../../entities/State.Entity';

interface ITcmReferralFormProps {
    referralId: number;
    states: Array<StateEntity>;
    // Telephonic Case Management
    tcmTelephonicCaseManagement?: TcmTelephonicCaseManagementEntity;
    handleTcmTelephonicCaseManagementEdit?: (tcm: TcmTelephonicCaseManagementEntity) => void;
    
    // Cover Letter
    coverLetterState?: TcmCoverLetterEntity;
    handleTcmCoverLetterEdit?: (edit: TcmCoverLetterEntity) => void;

    // Medical Info
    tcmMedicalInfo?: TcmMedicalInfoEntity;
    handleTcmMedicalInfoEdit?: (edit: TcmMedicalInfoEntity) => void;
        
    // Treating Provider
    tcmTreatingProvider?: TcmTreatingProviderEntity;
    handleTcmTreatingProviderEdit?: (provider: TcmTreatingProviderEntity) => void;
    
    // Office Visit
    tcmOfficeVisit?: TcmOfficeVisitEntity;
    handleTcmOfficeVisitEdit?: (visit: TcmOfficeVisitEntity) => void;
    
    // Work Status
    tcmWorkStatus?: TcmWorkStatusEntity;
    handleTcmWorkStatusEdit?: (status: TcmWorkStatusEntity) => void;
    
    handleSetValidationFunction?: (currentPageValidationFunction: () => boolean) => void;
    
    openCloseStatus?: Record<string, boolean>;
    handleOpenCloseStatus?: (boxName: string, isOpened: boolean) => void;
}

export const TcmReferralForm: FunctionComponent<ITcmReferralFormProps> = (props) => {
    const classes = styles();
    // Validation function (can be extended as needed)
    const validateForm = (): boolean => {
        // Add validation logic here if needed
        return true;
    };

    React.useEffect(() => {
        // Initialize open/close status if not set
        if (props.handleOpenCloseStatus && props.openCloseStatus) {
            if (!props.openCloseStatus.hasOwnProperty("TcmTelephonicCaseManagement_box")) {
                props.handleOpenCloseStatus("TcmTelephonicCaseManagement_box", true);
            }
            if (!props.openCloseStatus.hasOwnProperty("TcmCoverLetter_box")) {
                props.handleOpenCloseStatus("TcmCoverLetter_box", true);
            }
            if (!props.openCloseStatus.hasOwnProperty("TcmMedicalInfo_box")) {
                props.handleOpenCloseStatus("TcmMedicalInfo_box", true);
            }
            if (!props.openCloseStatus.hasOwnProperty("TcmTreatingProvider_box")) {
                props.handleOpenCloseStatus("TcmTreatingProvider_box", true);
            }
            if (!props.openCloseStatus.hasOwnProperty("TcmOfficeVisit_box")) {
                props.handleOpenCloseStatus("TcmOfficeVisit_box", true);
            }
            if (!props.openCloseStatus.hasOwnProperty("TcmWorkStatus_box")) {
                props.handleOpenCloseStatus("TcmWorkStatus_box", true);
            }
        }
    }, []);

    React.useEffect(() => {
        // Set validation function for parent component
        if (props.handleSetValidationFunction) {
            props.handleSetValidationFunction(validateForm);
        }
    }, []);

    const handleOpenCloseStatus = (event: React.MouseEvent, boxName: string) => {
        event.persist();
        if (props.handleOpenCloseStatus && props.openCloseStatus) {
            props.handleOpenCloseStatus(boxName, !(props.openCloseStatus?.[boxName] ?? true));
        }
    };

    return (
        <Fragment>
            <Grid item xs={12}>
                <Accordion 
                    className={classes.paper} 
                    expanded={props.openCloseStatus?.["TcmTelephonicCaseManagement_box"] ?? true}
                >
                    <AccordionSummary
                        expandIcon={<ExpandMoreIcon className={classes.paperHeaderIcon} />}
                        aria-controls="panel0a-content"
                        id="TcmTelephonicCaseManagement-accordion"
                        className={classes.paperHeader}
                        onClick={(event) => handleOpenCloseStatus(event, "TcmTelephonicCaseManagement_box")}
                    >
                        <Typography variant="subtitle2" className={classes.sidebarTitle}>
                            Telephonic Case Management
                        </Typography>
                    </AccordionSummary>
                    <AccordionDetails>
                        <TcmTelephonicCaseManagement
                            referralId={props.referralId}
                            tcmTelephonicCaseManagement={props.tcmTelephonicCaseManagement}
                            handleTcmTelephonicCaseManagementEdit={props.handleTcmTelephonicCaseManagementEdit}
                        />
                    </AccordionDetails>
                </Accordion>
            </Grid>

            

            <Grid item xs={12}>
                <Accordion 
                    className={classes.paper} 
                    expanded={props.openCloseStatus?.["TcmMedicalInfo_box"] ?? true}
                >
                    <AccordionSummary
                        expandIcon={<ExpandMoreIcon className={classes.paperHeaderIcon} />}
                        aria-controls="panel2a-content"
                        id="TcmMedicalInfo-accordion"
                        className={classes.paperHeader}
                        onClick={(event) => handleOpenCloseStatus(event, "TcmMedicalInfo_box")}
                    >
                        <Typography variant="subtitle2" className={classes.sidebarTitle}>
                            Medical & Diagnosis Information                        </Typography>
                    </AccordionSummary>
                    <AccordionDetails>
                        <TcmMedicalInfo
                            referralId={props.referralId}
                            tcmMedicalInfo={props.tcmMedicalInfo}
                            handleTcmMedicalInfoEdit={props.handleTcmMedicalInfoEdit}
                        />
                    </AccordionDetails>
                </Accordion>
            </Grid>

            <Grid item xs={12}>
                <Accordion 
                    className={classes.paper} 
                    expanded={props.openCloseStatus?.["TcmTreatingProvider_box"] ?? true}
                >
                    <AccordionSummary
                        expandIcon={<ExpandMoreIcon className={classes.paperHeaderIcon} />}
                        aria-controls="panel3a-content"
                        id="TcmTreatingProvider-accordion"
                        className={classes.paperHeader}
                        onClick={(event) => handleOpenCloseStatus(event, "TcmTreatingProvider_box")}
                    >
                        <Typography variant="subtitle2" className={classes.sidebarTitle}>
                            Treating Provider Information
                        </Typography>
                    </AccordionSummary>
                    <AccordionDetails>
                        <TcmTreatingProvider
                            referralId={props.referralId}
                            states={props.states}
                            tcmTreatingProvider={props.tcmTreatingProvider}
                            handleTcmTreatingProviderEdit={props.handleTcmTreatingProviderEdit}
                        />
                    </AccordionDetails>
                </Accordion>
            </Grid>

            <Grid item xs={12}>
                <Accordion 
                    className={classes.paper} 
                    expanded={props.openCloseStatus?.["TcmOfficeVisit_box"] ?? true}
                >
                    <AccordionSummary
                        expandIcon={<ExpandMoreIcon className={classes.paperHeaderIcon} />}
                        aria-controls="panel4a-content"
                        id="TcmOfficeVisit-accordion"
                        className={classes.paperHeader}
                        onClick={(event) => handleOpenCloseStatus(event, "TcmOfficeVisit_box")}
                    >
                        <Typography variant="subtitle2" className={classes.sidebarTitle}>
                            Office Visit Information
                        </Typography>
                    </AccordionSummary>
                    <AccordionDetails>
                        <TcmOfficeVisit
                            referralId={props.referralId}
                            tcmOfficeVisit={props.tcmOfficeVisit}
                            handleTcmOfficeVisitEdit={props.handleTcmOfficeVisitEdit}
                        />
                    </AccordionDetails>
                </Accordion>
            </Grid>

            <Grid item xs={12}>
                <Accordion 
                    className={classes.paper} 
                    expanded={props.openCloseStatus?.["TcmWorkStatus_box"] ?? true}
                >
                    <AccordionSummary
                        expandIcon={<ExpandMoreIcon className={classes.paperHeaderIcon} />}
                        aria-controls="panel5a-content"
                        id="TcmWorkStatus-accordion"
                        className={classes.paperHeader}
                        onClick={(event) => handleOpenCloseStatus(event, "TcmWorkStatus_box")}
                    >
                        <Typography variant="subtitle2" className={classes.sidebarTitle}>
                            Work Status Information
                        </Typography>
                    </AccordionSummary>
                    <AccordionDetails>
                        <TcmWorkStatus
                            referralId={props.referralId}
                            tcmWorkStatus={props.tcmWorkStatus}
                            handleTcmWorkStatusEdit={props.handleTcmWorkStatusEdit}
                        />
                    </AccordionDetails>
                </Accordion>
            </Grid>
            <Grid item xs={12}>
                <Accordion 
                    className={classes.paper} 
                    expanded={props.openCloseStatus?.["TcmCoverLetter_box"] ?? true}
                >
                    <AccordionSummary
                        expandIcon={<ExpandMoreIcon className={classes.paperHeaderIcon} />}
                        aria-controls="panel1a-content"
                        id="TcmCoverLetter-accordion"
                        className={classes.paperHeader}
                        onClick={(event) => handleOpenCloseStatus(event, "TcmCoverLetter_box")}
                    >
                        <Typography variant="subtitle2" className={classes.sidebarTitle}>
                            Cover Letter
                        </Typography>
                    </AccordionSummary>
                    <AccordionDetails>
                        <TcmCoverLetter
                            coverLetterState={props.coverLetterState}
                            handleTcmCoverLetterEdit={props.handleTcmCoverLetterEdit}
                        />
                    </AccordionDetails>
                </Accordion>
            </Grid>
        </Fragment>
    );
};

